import classNames from 'classnames'
import React, { ComponentProps, ComponentType, forwardRef, ReactNode, useMemo } from 'react'
import { Spinner } from './Spinner'
import { Tooltip } from 'react-tooltip'
import { KeyCommand } from './KeyCommand'

export type ButtonVariants =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'info'
  | 'warning'
  | 'danger'
  | 'ghost'
export type ButtonSizes = 'xxs' | 'xs' | 'sm'

export interface ButtonProps extends ComponentProps<'button'> {
  /**
   * The label to display within the button. Even if you don't want this to
   * display, you still need to provide a meaningful value for screen readers
   */
  label?: ReactNode
  /** Whether the label should be hidden */
  hideLabel?: boolean
  /**
   * The reason why the button is disabled. This is used to show a tooltip when
   * the button is hovered over.
   */
  disabledReason?: string
  /**
   * How the button should look, and what's it going to be used for.
   * Don't overpopulate a page with `primary` and `danger` buttons.
   */
  variant?: ButtonVariants
  /**
   * The size of the button, defaults to sm.
   */
  size?: ButtonSizes
  /**
   * Whether the button is loading. When true, a loading spinner will be displayed
   * and onClick events won't be fired. The width of the button stays the same
   */
  loading?: boolean
  /**
   * An icon to display before the label.
   */
  leadingIcon?: ComponentType<ComponentProps<'svg'>> | null
  /**
   * An icon to display after the label.
   */
  trailingIcon?: ComponentType<ComponentProps<'svg'>> | null
  /**
   * An optional keyboard shortcut to display next to the label
   */
  keyboardShortcut?: string
  /**
   * Whether this button should take up the full width of its container
   */
  fullWidth?: boolean
  /**
   * Whether the button should not be rounded
   */
  noRounding?: boolean
  /**
   * Whether the button should have a border
   */
  border?: boolean
  /**
   * Whether the button label should be prevented from wrapping
   */
  noWrap?: boolean
  /**
   * An optional count that will display next to the label
   */
  count?: number

  as?: React.ElementType
}

export const Button = forwardRef<any, ButtonProps>(
  (
    {
      as: As = 'button',
      label,
      disabled = false,
      disabledReason = '',
      hideLabel = false,
      variant = 'default',
      size = 'sm',
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      keyboardShortcut,
      loading = false,
      onClick,
      fullWidth = false,
      noWrap = false,
      noRounding = false,
      border = false,
      className = '',
      id,
      children,
      count = undefined,
      ...props
    },
    ref
  ) => {
    const handleClick = (e) => {
      if (onClick && !disabled && !loading) {
        onClick(e)
      }
    }

    const hasLabel = (!!label || children) && !hideLabel

    const resolvedLeadingIcon = useMemo(() => {
      if (!LeadingIcon) return null

      let className: string

      switch (size) {
        case 'xxs':
          className = (hasLabel ? 'mr-1 -ml-1' : '') + ' h-3 w-3'
          break

        case 'xs':
          className = (hasLabel ? 'mr-1 -ml-1' : '') + ' h-4 w-4'
          break

        case 'sm':
        default:
          className = (hasLabel ? 'mr-1 -ml-1.5' : '') + ' h-5 w-5 flex-shrink-0'
      }

      return <LeadingIcon aria-hidden className={className} />
    }, [LeadingIcon, hasLabel, size])

    const resolvedTrailingIcon = useMemo(() => {
      if (!TrailingIcon) return null

      switch (size) {
        case 'xxs':
          // eslint-disable-next-line react-hooks/exhaustive-deps
          className = (hasLabel ? 'ml-1' : '') + ' -mr-0.5 h-3 w-3'
          break

        case 'xs':
        case 'sm':
        default:
          className = (hasLabel ? 'ml-2' : '') + ' -mr-0.5 h-4 w-4'
      }

      return <TrailingIcon aria-hidden className={className} />
    }, [TrailingIcon, hasLabel])

    let classes = [
      'transition-colors',
      'relative',
      'overflow-hidden',
      'inline-flex',
      'items-center',
      'border',
      'font-medium',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-violet',
      'focus:z-10',
      'justify-center',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed',
    ]

    switch (size) {
      case 'xxs':
        classes = classes.concat(['px-2', 'py-1', 'text-sm', 'focus:ring-offset-1', 'text-xs'])

        if (noRounding === false) {
          classes.push('rounded-md')
        }

        break

      case 'xs':
        classes = classes.concat(['px-2', 'py-1', 'text-sm', 'focus:ring-offset-1'])

        if (noRounding === false) {
          classes.push('rounded-md')
        }

        break

      case 'sm':
        classes = classes.concat([
          LeadingIcon && hideLabel ? 'px-3' : 'px-4',
          'py-2',
          'h-9',
          'text-sm',
          'focus:ring-offset-2',
        ])

        if (noRounding === false) {
          classes.push('rounded-md')
        }

        break
    }

    switch (variant) {
      case 'default':
        classes = classes.concat([
          'border-border',
          'text-button-text',
          'bg-background',
          'shadow-sm',
          'hover:bg-background-muted',
          'disabled:hover:bg-white', // disable hover effect when disabled
          // 'dark:border-gray-700',
          // 'dark:text-gray-200',
          // 'dark:bg-gray-600',
          // 'dark:hover:bg-gray-700',
          // 'dark:border-transparent',
        ])
        break

      case 'primary':
        classes = classes.concat([
          'border-transparent',
          'text-white',
          'shadow',
          'bg-grape-700',
          'hover:bg-grape-500',
          'disabled:hover:bg-grape-700', // disable hover effect when disabled
          // 'dark:bg-grape-600',
        ])
        break

      case 'secondary':
        classes = classes.concat([
          border ? 'border-violet-200' : 'border-transparent',
          'text-violet-700',
          'bg-violet-100',
          'hover:bg-violet-200',
          'disabled:hover:bg-violet-200', // disable hover effect when disabled
          'dark:text-violet-100',
          'dark:bg-violet-500',
          'dark:hover:bg-violet-600',
        ])
        break

      case 'info':
        classes = classes.concat([
          border ? 'border-blue-200' : 'border-transparent',
          'border-transparent',
          'text-blue-800',
          'bg-blue-100',
          'hover:bg-blue-200',
          'disabled:hover:bg-blue-100', // disable hover effect when disabled
          'focus:ring-blue-300',
          'dark:text-blue-100',
          'dark:bg-blue-700',
          'dark:hover:bg-blue-800',
        ])
        break

      case 'warning':
        classes = classes.concat([
          border ? 'border-orange-200' : 'border-transparent',
          'border-transparent',
          'text-orange-800',
          'bg-orange-100',
          'hover:bg-orange-200',
          'disabled:hover:bg-orange-100', // disable hover effect when disabled
          'focus:ring-orange-300',
          'dark:text-orange-100',
          'dark:bg-orange-700',
          'dark:hover:bg-orange-800',
        ])
        break

      case 'danger':
        classes = classes.concat([
          border ? 'border-red-800' : 'border-transparent',
          'border-transparent',
          'text-white',
          'bg-red-600',
          'hover:bg-red-700',
          'disabled:hover:bg-red-700', // disable hover effect when disabled
          'focus:ring-red-500',
          'dark:text-gray-200',
          'dark:bg-red-700',
          'dark:hover:bg-red-800',
        ])
        break

      case 'ghost':
        classes = classes.concat([
          'border-transparent',
          '!shadow-none',
          'text-gray-700',
          'hover:bg-gray-50',
          'disabled:hover:bg-gray-50', // disable hover effect when disabled
          'dark:text-gray-200',
          'dark:bg-gray-600',
          'dark:hover:bg-gray-700',
        ])
        break
    }

    if (fullWidth) {
      classes.push('w-full')
    }

    const labelClasses = {
      'opacity-0': loading,
      'sr-only': hideLabel,
      'whitespace-nowrap': noWrap,
    }

    const hasTooltip = disabled && disabledReason
    const tooltipId = hasTooltip ? `disabled-button-tooltip-${Math.random() * 1000000}` : undefined

    return (
      <>
        <As
          type={As === 'button' ? 'button' : null}
          id={id}
          disabled={disabled || loading}
          onClick={(e) => handleClick(e)}
          className={classNames(className, classes)}
          {...props}
          title={hideLabel && typeof label === 'string' ? label : undefined}
          ref={ref}
          data-tooltip-content={disabledReason || undefined}
          data-tooltip-id={tooltipId}
          data-tooltip-place="top"
        >
          {resolvedLeadingIcon}

          <span className={classNames(labelClasses)}>{label}</span>

          {count !== undefined && (
            <div className="rounded-sm text-xs bg-gray-100 text-gray-700 ml-1 px-1">{count}</div>
          )}

          {resolvedTrailingIcon}

          {keyboardShortcut && (
            <div className="absolute right-2">
              <KeyCommand useSystemModifier={keyboardShortcut.startsWith('System+')}>
                {keyboardShortcut.replace('System+', '')}
              </KeyCommand>
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 bg-inherit flex justify-center items-center">
              <Spinner />
            </div>
          )}

          {children}
        </As>

        {hasTooltip && <Tooltip id={tooltipId} />}
      </>
    )
  }
)
